import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import cs from 'classnames'
import { useRouter } from 'next/router'
import { isFeatureEnabled } from '../../../../modules/features/selectors'
import { useSelector } from '../../../../react-redux'

import GridIcon from '../../../../ui/icons/grid'
import ListIcon from '../../../../ui/icons/list'
import { getSeriesURL, useLearningLibrary } from '../../../../graphql/learning'
import { AuthenticatedUser } from '../../../../types/userTypes'
import { selectAuthenticatedUser } from '../../../../modules/auth/selectors'
import SeriesList from '../../../../components/Learning/learning-modules/SeriesList'
import { ViewType } from '../../../../components/Learning/learning-modules/rows/types'
import { useSeriesCourseText } from '@/utils/series-course-text'

export const ListGridButtonGroup: React.FunctionComponent<{
  viewType
  setViewType
}> = ({ viewType, setViewType }) => {
  return (
    <div className="btn-group btn-group--fill" style={{ flexShrink: 0 }}>
      <button
        onClick={() => setViewType(ViewType.cards)}
        className={cs({ active: viewType === ViewType.cards })}
      >
        <GridIcon width={20} style={{ fill: 'white' }} />
      </button>
      <button
        onClick={() => setViewType(ViewType.table)}
        className={cs({ active: viewType === ViewType.table })}
      >
        <ListIcon width={20} style={{ fill: 'white' }} />
      </button>
    </div>
  )
}

export const useViewTypeFromSessionStorage = (): [
  ViewType,
  (viewType: ViewType) => void,
] => {
  const [viewType, setViewType] = useState(ViewType.cards)
  const SESSION_STORAGE_KEY = 'learning-library__view-type'
  useEffect(() => {
    setViewType(
      (typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem(SESSION_STORAGE_KEY) as ViewType)) ||
        ViewType.cards
    )
  }, [])

  const setViewTypeIncSessionStorage = (viewType: ViewType) => {
    setViewType(viewType)
    sessionStorage.setItem(SESSION_STORAGE_KEY, viewType)
  }

  return [viewType, setViewTypeIncSessionStorage]
}

const Page: React.FunctionComponent<{
  currentUser: AuthenticatedUser
  resourceID: string
  resourceType: string
  resource: any
}> = ({ resourceID, resourceType, resource, currentUser }) => {
  const router = useRouter()
  const { t } = useTranslation()

  const { data: dataModules, loading: loadingModules } = useLearningLibrary(
    resourceType,
    resourceID
  )

  const seriesList = useMemo(
    () =>
      dataModules?.series?.filter(
        singleSeries => singleSeries.modules.length
      ) ?? [],
    [dataModules]
  )

  if (loadingModules) {
    return (
      <div
        className="page__container fs-unmask"
        style={{ paddingTop: 20, paddingBottom: 20 }}
      >
        {t('common:ui.activity_indicator')}
        ...
      </div>
    )
  }

  if (seriesList.length === 0 && !resource?.target?.learning) {
    return (
      <div className="page__container fs-unmask" style={{ paddingTop: 20 }}>
        <div style={{ fontSize: 20 }}>No learning modules</div>
        <div>Please check back again later</div>
      </div>
    )
  }

  return (
    <div className="page__content">
      <div className="resource resource--group fs-unmask">
        <div
          className="resource resource--group fs-unmask"
          style={{ paddingTop: 0 }}
        >
          <SeriesList
            showDetails
            introHTML={
              resource?.target?.type === 'group'
                ? resource.target.learning_library_intro
                : undefined
            }
            languages={dataModules.languages ?? []}
            series={seriesList}
            onItemClick={singleSeries => {
              void router.push(getSeriesURL(singleSeries))
            }}
            showGroupFeatured={!!resource?.target?.learning}
            resourceType={resourceType}
            resourceID={resourceID}
          />
        </div>
      </div>
    </div>
  )
}

export default connect(store => ({
  currentUser: selectAuthenticatedUser(store),
}))(Page)
